import React, { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLabelPrint } from "@/context/LabelPrintContext";
import { formatDate } from "@/lib/format";
import { sharePdf, printPdf, logAcesso } from "@/lib/share";
import {
  ArrowDownToLine,
  Search,
  Loader2,
  Eye,
  ClipboardList,
  Share2,
  Undo2,
  MoreHorizontal,
  Printer,
  Tag,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { EntradaDetalheModal } from "@/components/EntradaDetalheModal";

/**
 * Lista de documentos de entrada (ENT-XXXX).
 * Renderizada abaixo do formulário de Entrada.
 * `refreshKey` força reload; incremente no parent após criar/agrupar rolos.
 */
export function EntradasSection({ refreshKey = 0 }) {
  const { hasPerm } = useAuth();
  const canSharePdf = hasPerm("entrada:compartilhar_pdf");
  const canPrintDoc = hasPerm("entrada:imprimir_documento");
  const canPrintLabels = hasPerm("entrada:imprimir_etiquetas");
  const hasAnyMais = canSharePdf || canPrintDoc || canPrintLabels;
  const { printLabels } = useLabelPrint();
  const [entradas, setEntradas] = useState([]);
  const [q, setQ] = useState("");
  const [fornecedorFiltro, setFornecedorFiltro] = useState("");
  const [ordem, setOrdem] = useState("data_desc");
  const [loading, setLoading] = useState(true);
  const [detalheId, setDetalheId] = useState(null);
  const [sharingId, setSharingId] = useState(null);
  const [printingLabelsId, setPrintingLabelsId] = useState(null);

  const load = () => {
    setLoading(true);
    api
      .get("/entradas")
      .then((r) => setEntradas(r.data))
      .catch((e) => {const _m=apiError(e); if(_m) toast.error(_m);})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [refreshKey]);

  const fornecedoresDe = (e) =>
    (e.fornecedores && e.fornecedores.length
      ? e.fornecedores
      : Array.from(
          new Set(
            (e.itens || [])
              .map((i) => (i.fornecedor_nome || "").trim())
              .filter(Boolean)
          )
        )) || [];

  const fornecedorLabel = (e) => {
    const arr = fornecedoresDe(e);
    return arr.length ? arr.join(", ") : "";
  };

  const fornecedoresDisponiveis = useMemo(() => {
    const set = new Set();
    entradas.forEach((e) => fornecedoresDe(e).forEach((f) => set.add(f)));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [entradas]);

  const filtradas = useMemo(() => {
    const t = q.trim().toLowerCase();
    let arr = entradas.filter((e) => {
      if (fornecedorFiltro) {
        if (!fornecedoresDe(e).includes(fornecedorFiltro)) return false;
      }
      if (!t) return true;
      if ((e.codigo || "").toLowerCase().includes(t)) return true;
      if ((e.nota_fiscal || "").toLowerCase().includes(t)) return true;
      if ((e.criado_por_nome || "").toLowerCase().includes(t)) return true;
      if (
        (e.itens || []).some(
          (i) =>
            (i.codigo || "").toLowerCase().includes(t) ||
            (i.tipo || "").toLowerCase().includes(t) ||
            (i.cor || "").toLowerCase().includes(t) ||
            (i.fornecedor_nome || "").toLowerCase().includes(t)
        )
      )
        return true;
      return false;
    });
    arr = [...arr].sort((a, b) => {
      const cmp = (a.data || "").localeCompare(b.data || "");
      return ordem === "data_asc" ? cmp : -cmp;
    });
    return arr;
  }, [entradas, q, fornecedorFiltro, ordem]);

  const totalRolos = useMemo(
    () => filtradas.reduce((s, e) => s + (e.total_rolos || 0), 0),
    [filtradas]
  );

  const compartilhar = async (e) => {
    setSharingId(e.id);
    try {
      await sharePdf(
        `/entradas/${e.id}/pdf`,
        `Entrada - ${e.codigo}.pdf`,
        `Entrada ${e.codigo}`
      );
      logAcesso({ tipo: "ent_compartilhado", documento_tipo: "entrada", documento_id: e.id, documento_codigo: e.codigo });
    } finally {
      setSharingId(null);
    }
  };

  const imprimirDocumento = (e) => {
    printPdf(`/entradas/${e.id}/pdf`);
    logAcesso({ tipo: "ent_impresso_doc", documento_tipo: "entrada", documento_id: e.id, documento_codigo: e.codigo });
  };

  const imprimirEtiquetas = async (e) => {
    setPrintingLabelsId(e.id);
    try {
      const res = await api.get(`/entradas/${e.id}/rolos`);
      const rolos = res.data || [];
      if (!rolos.length) {
        toast.error("Este documento não tem rolos para imprimir.");
        return;
      }
      printLabels(rolos);
      logAcesso({ tipo: "ent_impresso_etiquetas", documento_tipo: "entrada", documento_id: e.id, documento_codigo: e.codigo, qtd_rolos: rolos.length });
    } catch (err) {
      const _m = apiError(err);
      if (_m) toast.error(_m);
    } finally {
      setPrintingLabelsId(null);
    }
  };

  return (
    <section
      className="rounded-md border-2 border-slate-200 bg-white p-5"
      data-testid="entradas-section"
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900">
          <ClipboardList size={20} /> Documentos de entrada
        </h2>
        <div className="flex items-center gap-3 rounded-md bg-green-700 px-3 py-1.5 text-white">
          <div className="text-sm">
            <span className="font-mono font-bold">{filtradas.length}</span>
            <span className="ml-1 text-green-100">
              doc{filtradas.length === 1 ? "" : "s"}
            </span>
          </div>
          <div className="text-sm">
            <span className="font-mono font-bold">{totalRolos}</span>
            <span className="ml-1 text-green-100">
              rolo{totalRolos === 1 ? "" : "s"}
            </span>
          </div>
        </div>
      </div>

      <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
        <div className="relative sm:col-span-1">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            className="h-11 w-full rounded-md border-2 border-slate-300 pl-9 pr-3 text-sm outline-none focus:border-blue-500"
            placeholder="Buscar por ENT, NF, rolo, tipo, cor…"
            value={q}
            onChange={(ev) => setQ(ev.target.value)}
            data-testid="entradas-busca"
          />
        </div>
        <select
          className="h-11 rounded-md border-2 border-slate-300 bg-white px-3 text-sm outline-none focus:border-blue-500"
          value={fornecedorFiltro}
          onChange={(ev) => setFornecedorFiltro(ev.target.value)}
          data-testid="entradas-filtro-fornecedor"
        >
          <option value="">Todos os fornecedores</option>
          {fornecedoresDisponiveis.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
        <select
          className="h-11 rounded-md border-2 border-slate-300 bg-white px-3 text-sm outline-none focus:border-blue-500"
          value={ordem}
          onChange={(ev) => setOrdem(ev.target.value)}
          data-testid="entradas-ordem"
        >
          <option value="data_desc">Mais recentes primeiro</option>
          <option value="data_asc">Mais antigos primeiro</option>
        </select>
      </div>

      <div className="overflow-x-auto rounded-md border-2 border-slate-100">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-slate-100 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-3 py-2">Documento</th>
              <th className="px-3 py-2">Data</th>
              <th className="px-3 py-2">Rolos</th>
              <th className="px-3 py-2">Fornecedor</th>
              <th className="px-3 py-2">NF</th>
              <th className="px-3 py-2 text-right">Ações</th>
            </tr>
          </thead>
          <tbody data-testid="entradas-table">
            {loading && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-slate-400">
                  <Loader2 className="mx-auto animate-spin" />
                </td>
              </tr>
            )}
            {!loading && filtradas.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-400">
                  Nenhum documento de entrada encontrado.
                </td>
              </tr>
            )}
            {!loading &&
              filtradas.map((e) => {
                const isDev = e.modo === "devolucao" || e.origem_tipo === "devolucao";
                return (
                <tr
                  key={e.id}
                  className="border-b border-slate-100 last:border-0 hover:bg-slate-50"
                  data-testid={`entrada-row-${e.codigo}`}
                >
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className={`flex h-8 w-8 items-center justify-center rounded-md ${isDev ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>
                        {isDev ? <Undo2 size={15} /> : <ArrowDownToLine size={15} />}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <div className="font-mono font-bold text-slate-900">
                            {e.codigo}
                          </div>
                          {isDev && (
                            <span
                              className="rounded-sm bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-800"
                              data-testid={`entrada-devolucao-badge-${e.codigo}`}
                            >
                              Devolução
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500">
                          {isDev
                            ? `Devolução da REF ${e.ref_origem || "—"}`
                            : e.modo === "lote"
                              ? "Lote"
                              : e.modo === "misto"
                                ? "Misto"
                                : "Individual"}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {formatDate(e.data)}
                  </td>
                  <td className="px-3 py-2 font-semibold">{e.total_rolos}</td>
                  <td className="px-3 py-2 text-slate-600">
                    {fornecedorLabel(e) || (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-600">
                    {e.nota_fiscal || <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setDetalheId(e.id)}
                        data-testid={`entrada-ver-${e.codigo}`}
                        className="flex items-center gap-1 rounded-md border-2 border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                      >
                        <Eye size={14} /> Ver
                      </button>
                      {hasAnyMais && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              data-testid={`entrada-mais-${e.codigo}`}
                              className="flex items-center gap-1 rounded-md border-2 border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                              aria-label="Mais ações"
                            >
                              <MoreHorizontal size={14} /> Mais
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-56">
                            {canSharePdf && (
                              <DropdownMenuItem
                                onSelect={() => compartilhar(e)}
                                disabled={sharingId === e.id}
                                data-testid={`entrada-share-${e.codigo}`}
                                className="gap-2 py-2 text-sm"
                              >
                                {sharingId === e.id ? (
                                  <Loader2 size={16} className="animate-spin" />
                                ) : (
                                  <Share2 size={16} />
                                )}
                                Compartilhar
                              </DropdownMenuItem>
                            )}
                            {canPrintDoc && (
                              <DropdownMenuItem
                                onSelect={() => imprimirDocumento(e)}
                                data-testid={`entrada-print-doc-${e.codigo}`}
                                className="gap-2 py-2 text-sm"
                              >
                                <Printer size={16} /> Imprimir documento
                              </DropdownMenuItem>
                            )}
                            {canPrintLabels && (
                              <DropdownMenuItem
                                onSelect={() => imprimirEtiquetas(e)}
                                disabled={printingLabelsId === e.id}
                                data-testid={`entrada-print-labels-${e.codigo}`}
                                className="gap-2 py-2 text-sm"
                              >
                                {printingLabelsId === e.id ? (
                                  <Loader2 size={16} className="animate-spin" />
                                ) : (
                                  <Tag size={16} />
                                )}
                                Imprimir etiquetas
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      <EntradaDetalheModal
        entradaId={detalheId}
        open={!!detalheId}
        onClose={() => setDetalheId(null)}
      />
    </section>
  );
}
